import { ChangeEvent } from 'react'
import { toast } from 'react-toastify'

import {
  Button, Form, SidebarModal,
} from '@domain-group/suit-theme'

import useAssignCampaignTeamOrAgents from '@hooks/useAssignCampaignTeamOrAgents'

import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'

import AgentOrTeamSearch from '@components/AgentOrTeamSearch'
import SelectedAgentsAndTeam from '@components/SelectedAgentsAndTeam'

import EntitySelectBox from '@components/EntitySelectBox'
import useSelector from '@hooks/useSelector'

import type { CampaignModel } from '@models/types'
import type { EntitiesState } from '@redux/entities'

import useAgencyMarketingCampaign from '../../hooks/useAgencyMarketingCampaign'

type HandleEntityChangeParams = {
  agencies: EntitiesState['agencies'],
  e: ChangeEvent<HTMLSelectElement>,
  geoCountries: EntitiesState['geoCountries'],
  setEntityState: (state: Partial<CampaignModel>) => void,
  title: string,
}

const handleEntityChange = (params: HandleEntityChangeParams) => {
  const {
    agencies, e, geoCountries, setEntityState, title,
  } = params

  const agencyId: number = Number(e.target.value)
  const agency = agencies[agencyId]

  setEntityState({
    agency_id: agencyId,
    country_code: geoCountries[Number(agency?.geo_country_id)]?.code || '',
    post_code: agency?.address_postcode || '',
    state_name: agency?.address_state || '',
    street_name: title || agency?.address_line_1,
    street_number: '[AM]',
    suburb_name: agency?.address_region || '',
  })
}

interface NewCampaignProps {
  agencyMarketingCampaignParams: {
    categoryId: number,
    isCanvaTemplate?: boolean,
    selectedAgencyId: number,
    selectedProviderPriceListItemId: number,
    selectedProviderPriceListItemOptions: number,
    selectedTemplatePageId: number,
  },
  callbacks: {
    closeModal: () => void,
    createArtworkWithCampaign: (payload: HandleCallbackActionParams) => void,
  },
  createCampaignWithArtwork: boolean,
}

const NewCampaign = (props: NewCampaignProps) => {
  const {
    agencyMarketingCampaignParams,
    callbacks: { createArtworkWithCampaign },
    createCampaignWithArtwork,
  } = props

  const agencyMarketingCampaignPayload = useAgencyMarketingCampaign(agencyMarketingCampaignParams)
  const {
    agencyMarketingPayload,
    callbacks: {
      createCampaign: createCampaignFn,
      createArtworkWithCampaign: createArtworkWithCampaignFn,
    },
    campaignFormPayload,
    creatingArtwork,
    creatingCampaign,
    isCanvaTemplate,
    saveEnabled,
  } = agencyMarketingCampaignPayload

  const {
    entityState,
    entityState: {
      agency_id,
      title,
    },
    handlers,
    setEntityState,
  } = campaignFormPayload

  const assignCampaignTeamOrAgentsPayload = useAssignCampaignTeamOrAgents({ campaignFormPayload })

  const {
    callbacks: {
      selectAgentOrTeam,
    },
  } = assignCampaignTeamOrAgentsPayload

  const entityParams = createCampaignWithArtwork
    ? { ...agencyMarketingPayload, campaign: { ...entityState, agents: entityState.agent_ids } }
    : { ...entityState, agents: entityState.agent_ids, agency_marketing: true }

  const { agencies, geoCountries } = useSelector(reduxState => reduxState.entities)

  const createPayload = {
    callbacks: {
      action: createCampaignWithArtwork ? createArtworkWithCampaignFn : createCampaignFn,
      afterAction: ({ response }: { response: any }) => {
        if (!response.success) return

        if (createCampaignWithArtwork && isCanvaTemplate){
          const campaignId = response?.data?.campaign_id
          const orderItemId = response?.data?.order_item_id
          const artworkId = response?.data?.id

          if (campaignId && orderItemId){
            toast.info('Please wait... Canva artwork is being created.')
            const canvaParams = `canva_order_item_id=${orderItemId}&canva_artwork_id=${artworkId}`
            const pendingOrdersUrl = `#/campaigns/${campaignId}/edit/orders/pending?${canvaParams}`
            window.location.href = pendingOrdersUrl
            return
          }

          if (response?.redirectUrl){
            window.location.href = response.redirectUrl
          }

          return
        }

        if (response.success && !createCampaignWithArtwork){
          const pendingCampaignOrdersUrl = `#/campaigns/${response?.data?.id}/edit/orders/pending`
          window.location.href = pendingCampaignOrdersUrl
        }
      },
    },
    entityParams,
    shouldRedirect: createCampaignWithArtwork ? !isCanvaTemplate : true,
    toastText: 'Campaign Created',
  }

  return (
    <>
      <SidebarModal.Content>
        <Form>
          <Form.Field
            direction="column"
            label="* Title"
          >
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          {!agencyMarketingCampaignParams?.selectedAgencyId && (
            <Form.Field
              label="* Select Agency"
              marginTop={agencyMarketingCampaignParams?.selectedAgencyId ? 'none' : 'large'}
            >
              <EntitySelectBox
                data-value-type="number"
                entityKey="agencies"
                // @ts-expect-error TS2322
                name="agency_id"
                value={agency_id}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleEntityChange({
                  agencies, e, geoCountries, setEntityState, title,
                })}
              />
            </Form.Field>
          )}

          <Form.Field direction="column" label="* Assign Teams & Agents" marginTop="large">
            <AgentOrTeamSearch
              callbacks={{ selectAgentOrTeam }}
              selectedAgentIds={entityState.agent_ids || []}
              selectedTeamId={entityState.team_id}
            />
          </Form.Field>

          <SelectedAgentsAndTeam
            assignCampaignTeamOrAgentsPayload={assignCampaignTeamOrAgentsPayload}
          />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creatingArtwork || creatingCampaign}
          // @ts-expect-error TS2345
          onClick={() => createArtworkWithCampaign(createPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Campaign' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </>
  )
}

export default NewCampaign
